import { createSignal, type JSX } from "solid-js";
import { render } from "solid-js/web";
import { InteractiveTableView } from "../components/InteractiveTableView.tsx";
import {
	type ParsedTableCell,
	parseTableCell,
	tableRowElements,
} from "./parseTableElement";
import {
	headingBefore,
	hydrateMentionPlaceholders,
	tableHasHeaderRow,
} from "./richTextDomHydration.tsx";
import { splitSettledMarkdown, trimBlankLines } from "./splitSettledMarkdown";

export type MarkdownChunkHtml = {
	html: string;
	/** Mention mounts keyed by the `data-mention-placeholder` ids in `html`. */
	placeholders: Map<string, () => JSX.Element>;
};

type RowRecord = {
	/** The row's rendered markup before hydration, to tell an unchanged row. */
	signature: string;
	cells: ParsedTableCell[];
	disposers: Array<() => void>;
};

type StreamingTable = {
	mountPoint: HTMLDivElement;
	update: (
		table: HTMLTableElement,
		placeholders: MarkdownChunkHtml["placeholders"],
	) => void;
	/** Takes the heading before the mounted table as its title; see {@link headingBefore}. */
	adoptHeading: () => void;
	dispose: () => void;
};

type RenderedChunk = {
	nodes: Node[];
	disposers: Array<() => void>;
	tables: StreamingTable[];
};

const runAll = (disposers: Array<() => void>) => {
	for (const dispose of disposers.splice(0)) {
		dispose();
	}
};

/**
 * An {@link InteractiveTableView} that stays mounted while its markdown is
 * re-rendered, keeping each row whose markup hasn't changed. As a table streams
 * in, only the row being written is rebuilt.
 */
function createStreamingTable(
	id: string,
	theme: "standard" | "borderless",
	allowFullWidth: boolean,
): StreamingTable {
	const [headers, setHeaders] = createSignal<ParsedTableCell[]>([]);
	const [rows, setRows] = createSignal<ParsedTableCell[][]>([]);
	const [heading, setHeading] = createSignal<HTMLElement>();
	let header: RowRecord | undefined;
	let body: RowRecord[] = [];
	let hasHeaderRow = false;

	const mountPoint = document.createElement("div");
	mountPoint.style.display = "contents";
	const disposeView = render(
		() => (
			<InteractiveTableView
				id={id}
				theme={theme}
				allowFullWidth={allowFullWidth}
				heading={heading()}
				headers={headers()}
				rows={rows()}
			/>
		),
		mountPoint,
	);

	const reconcileRow = (
		previous: RowRecord | undefined,
		row: HTMLTableRowElement,
		placeholders: MarkdownChunkHtml["placeholders"],
	): RowRecord => {
		const signature = row.innerHTML;
		if (previous?.signature === signature) {
			return previous;
		}
		if (previous) {
			runAll(previous.disposers);
		}
		const disposers: Array<() => void> = [];
		hydrateMentionPlaceholders(row, placeholders, disposers);
		return {
			signature,
			cells: Array.from(row.cells).map(parseTableCell),
			disposers,
		};
	};

	return {
		mountPoint,
		update: (table, placeholders) => {
			hasHeaderRow = tableHasHeaderRow(table);
			const rowElements = tableRowElements(table);
			if (rowElements.header) {
				header = reconcileRow(header, rowElements.header, placeholders);
			} else if (header) {
				runAll(header.disposers);
				header = undefined;
			}
			const nextBody = rowElements.body.map((row, i) =>
				reconcileRow(body[i], row, placeholders),
			);
			for (const removed of body.slice(rowElements.body.length)) {
				runAll(removed.disposers);
			}
			body = nextBody;
			setHeaders(header?.cells ?? []);
			setRows(body.map((record) => record.cells));
		},
		adoptHeading: () => {
			const before = hasHeaderRow ? headingBefore(mountPoint) : undefined;
			if (before) {
				setHeading(before);
			} else if (!heading()?.isConnected) {
				// The heading re-rendered with the tail and was removed with it.
				setHeading(undefined);
			}
		},
		dispose: () => {
			disposeView();
			if (header) {
				runAll(header.disposers);
			}
			for (const record of body) {
				runAll(record.disposers);
			}
			mountPoint.remove();
		},
	};
}

/**
 * Renders streamed markdown into `container`, appending blocks as they settle
 * rather than replacing everything on each update. Only the unsettled tail is
 * re-rendered, and tables in it stay mounted (see {@link splitSettledMarkdown}).
 *
 * Expects append-only markdown; anything else is rendered again from scratch.
 */
export function createIncrementalMarkdownView(
	container: HTMLElement,
	options: {
		tableIdBase: string;
		theme: "standard" | "borderless";
		/** See {@link InteractiveTableView}'s `allowFullWidth`. */
		allowFullWidth?: boolean;
		toHtml: (markdown: string) => MarkdownChunkHtml;
	},
) {
	let markdown = "";
	let settledLength = 0;
	let settled: RenderedChunk[] = [];
	let tail: RenderedChunk | undefined;
	/**
	 * Tables the tail stopped rendering. A table drops out while its header row
	 * is half-written (`| Name | V` isn't a table row, `| Name |` is), so it's
	 * kept to be taken over when the row completes rather than remounted.
	 */
	let spareTables: StreamingTable[] = [];
	let tableCount = 0;

	const removeChunk = (chunk: RenderedChunk) => {
		for (const node of chunk.nodes) {
			node.parentNode?.removeChild(node);
		}
		runAll(chunk.disposers);
	};

	/** Renders `text` and appends it, taking over `reusable` tables in order. */
	const renderChunk = (
		text: string,
		reusable: StreamingTable[],
	): RenderedChunk => {
		const { html, placeholders } = options.toHtml(trimBlankLines(text));
		const holder = document.createElement("div");
		holder.innerHTML = html;

		// Tables hydrate their own mentions row by row, so a kept row keeps its
		// mounted mentions; this has to happen before the chunk-wide pass below.
		const tables: StreamingTable[] = [];
		for (const table of holder.querySelectorAll("table")) {
			const streaming =
				reusable.shift() ??
				createStreamingTable(
					`${options.tableIdBase}-tbl-${tableCount++}`,
					options.theme,
					options.allowFullWidth ?? false,
				);
			streaming.update(table, placeholders);
			table.replaceWith(streaming.mountPoint);
			tables.push(streaming);
		}

		const disposers: Array<() => void> = [];
		hydrateMentionPlaceholders(holder, placeholders, disposers);

		const nodes = Array.from(holder.childNodes);
		container.append(...nodes);
		// After appending, as the heading may be in an earlier, settled block.
		for (const table of tables) {
			table.adoptHeading();
		}
		return { nodes, disposers, tables };
	};

	const reset = () => {
		for (const chunk of [...settled, ...(tail ? [tail] : [])]) {
			removeChunk(chunk);
			for (const table of chunk.tables) {
				table.dispose();
			}
		}
		for (const table of spareTables.splice(0)) {
			table.dispose();
		}
		settled = [];
		tail = undefined;
		settledLength = 0;
		markdown = "";
		container.replaceChildren();
	};

	const update = (next: string) => {
		if (next === markdown) {
			return;
		}
		if (!next.startsWith(markdown.slice(0, settledLength))) {
			reset();
		}
		markdown = next;

		const { settled: newlySettled, tail: tailText } = splitSettledMarkdown(
			next.slice(settledLength),
		);
		const reusable = [...(tail?.tables ?? []), ...spareTables];
		if (tail) {
			removeChunk(tail);
			tail = undefined;
		}

		for (const block of newlySettled) {
			settled.push(renderChunk(block, reusable));
			settledLength += block.length;
		}
		// A tail of blank lines would only render as an empty paragraph.
		if (tailText.trim().length > 0) {
			tail = renderChunk(tailText, reusable);
		}

		spareTables = reusable;
		if (newlySettled.length > 0) {
			for (const unused of spareTables.splice(0)) {
				unused.dispose();
			}
		}
	};

	return { update, dispose: reset };
}
